import { useEffect, useState } from 'react';
import { FaBoxOpen, FaCashRegister, FaCreditCard, FaSearch } from 'react-icons/fa';
import { FaMoneyBillWave } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import AdminPOSCartItem from '../components/AdminPOSCartItem';
import POSSuccessModal from '../components/POSSuccessModal';
import displayNPRCurrency from '../helpers/displayCurrency';
import { request } from '../helpers/request';

const AdminPOS = () => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    // POS Cart State (Local)
    const [cart, setCart] = useState([]);
    const [showCart, setShowCart] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('CASH');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successOrder, setSuccessOrder] = useState(null);

    // Fetch Products & Categories
    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await request(SummaryApi.allProduct);
            const data = await response.json();
            
            if(data.success) {
                setProducts(data.data || []);
                setFilteredProducts(data.data || []);
                
                // Extract Categories
                const cats = ['all', ...new Set(data.data.map(p => p.category))];
                setCategories(cats);
            }
        } catch (error) {
            toast.error("Failed to load products");
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    // Filter Logic
    useEffect(() => {
        let result = products;

        if (selectedCategory !== 'all') {
            result = result.filter(p => p.category === selectedCategory);
        }

        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            result = result.filter(p => 
                p.productName.toLowerCase().includes(lowerQuery) || 
                p.category.toLowerCase().includes(lowerQuery)
            );
        }

        setFilteredProducts(result);
    }, [selectedCategory, searchQuery, products]);

    // Cart Actions
    const addToCart = (product) => {
        if (!product?.stock || product.stock <= 0) {
            toast.error("Out of stock!");
            return;
        }

        setCart(prev => {
            const existing = prev.find(item => item._id === product._id);
            if (existing) {
                if(existing.quantity >= product.stock) {
                    toast.warning("Max stock reached");
                    return prev;
                }
                return prev.map(item => 
                    item._id === product._id 
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
        toast.success("Added to cart", { autoClose: 500, hideProgressBar: true });
    };

    const removeFromCart = (id) => {
        setCart(prev => prev.filter(item => item._id !== id));
    };

    const updateQuantity = (id, newQty) => {
        setCart(prev => prev.map(item => {
            if (item._id === id) {
                // Find original product to check stock
                // Note: In a real app we might need to lookup from 'products' array if 'item' doesn't have full stock info, 
                // but our item is a copy of product, so it has stock.
                if (newQty > item.stock) {
                    toast.warning(`Only ${item.stock} available`);
                    return item;
                }
                return { ...item, quantity: Math.max(1, newQty) };
            }
            return item;
        }));
    };
    
    // Checkout
    const handleCheckout = async () => {
        if (cart.length === 0) return;
        setProcessing(true);

        const totalAmount = cart.reduce((sum, item) => sum + (item.sellingPrice * item.quantity), 0);
        
        const orderData = {
            cartItems: cart.map(item => ({
                productId: item._id,
                quantity: item.quantity
            })),
            paymentMethod,
            totalAmount
        };

        try {
            const response = await request(SummaryApi.createAdminOrder, {
                method: 'POST',
                body: JSON.stringify(orderData)
            });
            const data = await response.json();

            if (data.success) {
                setSuccessOrder(data.data);
                setShowSuccessModal(true);
                setCart([]);
                setShowCart(false);
                fetchProducts(); // Refresh stock
            } else {
                toast.error(data.message || "Transaction failed");
            }
        } catch (error) {
            toast.error("Network error");
        }
        setProcessing(false);
    };

    const cartTotal = cart.reduce((sum, item) => sum + (item.sellingPrice * item.quantity), 0);
    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <div className='h-[calc(100vh-60px)] flex flex-col bg-gray-50 relative overflow-hidden'>
            {/* Header / Search */}
            <div className='p-4 bg-white border-b border-gray-200 shadow-sm z-10 sticky top-0'>
                <div className='relative mb-4'>
                    <div className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none'>
                        <FaSearch size={18} />
                    </div>
                    <input 
                        type="text" 
                        placeholder="Search products..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className='w-full pl-12 pr-4 py-3.5 bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium text-base placeholder:text-gray-400'
                    />
                </div>
                
                {/* Categories */}
                <div className='flex gap-2 overflow-x-auto scrollbar-none pb-1'>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className={`px-5 py-2.5 rounded-full text-xs font-bold whitespace-nowrap transition-all border ${
                                selectedCategory === cat 
                                ? 'bg-primary text-white border-primary shadow-lg shadow-primary/30' 
                                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            {cat.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Product Grid */}
            <div className='flex-1 overflow-y-auto p-4 pb-32 bg-gray-50'>
                {loading ? (
                    <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse'>
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className='bg-white h-64 rounded-2xl'></div>
                        ))}
                    </div>
                ) : (
                    <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 content-start'>
                        {filteredProducts.map(product => {
                            const isOutOfStock = !product.stock || product.stock <= 0;
                            return (
                                <div 
                                    key={product._id} 
                                    className={`bg-white rounded-3xl p-3 border shadow-sm flex flex-col h-full transition-all duration-200 overflow-hidden relative group ${
                                        isOutOfStock 
                                        ? 'border-red-100' 
                                        : 'border-gray-100 hover:border-primary/30 hover:shadow-md active:scale-[0.98] cursor-pointer'
                                    }`} 
                                    onClick={() => !isOutOfStock && addToCart(product)}
                                >
                                    {/* Image Container */}
                                    <div className='aspect-square mb-3 bg-gray-50 rounded-2xl p-4 relative overflow-hidden'>
                                        <img 
                                            src={product.productImage[0]} 
                                            className={`w-full h-full object-contain mix-blend-multiply transition-transform duration-500 ${!isOutOfStock && 'group-hover:scale-110'}`} 
                                            alt={product.productName} 
                                        />
                                        
                                        {/* Out of Stock Overlay */}
                                        {isOutOfStock && (
                                            <div className='absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-10'>
                                                <div className='bg-red-500 text-white text-[10px] font-black px-3 py-1.5 rounded-full shadow-lg transform -rotate-6 border-2 border-white'>
                                                    OUT OF STOCK
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className='flex-1 flex flex-col min-h-[80px]'>
                                        <h3 className={`font-bold text-gray-800 line-clamp-2 mb-3 leading-snug ${product.productName.length < 20 ? 'text-base' : 'text-sm'}`}>
                                            {product.productName || "Unnamed Product"}
                                        </h3>
                                        
                                        <div className='mt-auto flex flex-col gap-2'>
                                            <div className='flex flex-col'>
                                                <p className='text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5'>Price</p>
                                                <span className={`font-black text-lg leading-none ${isOutOfStock ? 'text-gray-400' : 'text-primary'}`}>
                                                    {displayNPRCurrency(product.sellingPrice)}
                                                </span>
                                            </div>
                                            
                                            <div className='flex items-center justify-between'>
                                                <p className='text-[10px] font-bold text-gray-400 uppercase tracking-wider'>Stock</p>
                                                {!isOutOfStock ? (
                                                    <div className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg ${
                                                        product.stock < 5 
                                                        ? 'bg-orange-100 text-orange-700' 
                                                        : 'bg-green-100 text-green-700'
                                                    }`}>
                                                        {product.stock} Units
                                                    </div>
                                                ) : (
                                                    <div className='flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg bg-gray-100 text-gray-400'>
                                                        0 Units
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {!loading && filteredProducts.length === 0 && (
                    <div className='flex flex-col items-center justify-center h-full text-gray-400'>
                        <FaBoxOpen size={48} className='mb-4 opacity-50' />
                        <p>No products found</p>
                    </div>
                )}
            </div>

            {/* Bottom Cart Summary (Sticky) */}
            {cart.length > 0 && (
                <div className='absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] z-20'>
                    <div className='flex justify-between items-center mb-0'>
                        <div onClick={() => setShowCart(true)} className='flex items-center gap-3 cursor-pointer'>
                            <div className='w-12 h-12 bg-primary text-white rounded-xl flex items-center justify-center text-lg font-bold shadow-lg relative'>
                                {cartCount}
                                <span className='absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white'></span>
                            </div>
                            <div>
                                <p className='text-xs text-gray-500 font-bold uppercase'>Total</p>
                                <p className='text-xl font-black text-gray-800'>{displayNPRCurrency(cartTotal)}</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => setShowCart(true)} 
                            className='bg-primary text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-primary/30 active:scale-95 transition-transform'
                        >
                            View Cart
                        </button>
                    </div>
                </div>
            )}

            {/* Cart Drawer / Modal */}
            {showCart && (
                <div className='fixed inset-0 z-[100]'>
                    <div className='absolute inset-0 bg-black/60 backdrop-blur-sm' onClick={() => setShowCart(false)}></div>
                    <div className='absolute bottom-0 left-0 right-0 h-[85vh] bg-white rounded-t-[2rem] shadow-2xl flex flex-col animate-slide-up'>
                        {/* Drawer Header */}
                        <div className='p-6 border-b border-gray-100 flex justify-between items-center'>
                            <h2 className='text-2xl font-black text-gray-800 tracking-tight'>Current Transaction</h2>
                            <button onClick={() => {setShowCart(false); }} className='w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200'>✕</button>
                        </div>

                        {/* Cart Items */}
                        <div className='flex-1 overflow-y-auto p-6 space-y-4'>
                            {cart.map(item => (
                                <AdminPOSCartItem 
                                    key={item._id} 
                                    item={item} 
                                    updateQuantity={(id, qty) => updateQuantity(id, qty)} 
                                    removeItem={removeFromCart} 
                                />
                            ))}
                        </div>

                        {/* Payment & Checkout */}
                        <div className='p-6 bg-gray-50 border-t border-gray-200'>
                            <div className='mb-6'>
                                <p className='text-xs font-bold text-gray-500 uppercase mb-3'>Payment Method</p>
                                <div className='grid grid-cols-2 gap-3'>
                                    <button 
                                        onClick={() => setPaymentMethod('CASH')}
                                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border border-gray-200 font-bold transition-all ${paymentMethod === 'CASH' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-white'}`}
                                    >
                                        <FaMoneyBillWave /> Cash
                                    </button>
                                    <button 
                                        onClick={() => setPaymentMethod('CARD')}
                                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border border-gray-200 font-bold transition-all ${paymentMethod === 'CARD' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-white'}`}
                                    >
                                        <FaCreditCard /> Card
                                    </button>
                                </div>
                            </div>

                            <div className='flex justify-between items-center mb-6'>
                                <span className='text-lg font-bold text-gray-600'>Total Amount</span>
                                <span className='text-3xl font-black text-primary'>{displayNPRCurrency(cartTotal)}</span>
                            </div>

                            <button 
                                onClick={handleCheckout}
                                disabled={processing}
                                className='w-full bg-primary text-white h-14 rounded-xl font-black text-lg shadow-xl shadow-primary/20 hover:bg-accent active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed'
                            >
                                {processing ? (
                                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                ) : (
                                    <>
                                        <FaCashRegister /> Complete Transaction
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Success Modal */}
            <POSSuccessModal 
                isOpen={showSuccessModal} 
                onClose={() => setShowSuccessModal(false)}
                orderData={successOrder}
                onPrint={() => window.print()} 
            />
        </div>
    );
};

export default AdminPOS;
